import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild, TemplateRef } from '@angular/core';
import { AppService, CoreTypes, LabelValue, Modal } from 'common-cloudstudio';
import { ToasterService } from 'angular2-toaster';

export interface ColumnConfig {
  name: string;
  property: string;
  type?: string;
  renderer?: 'badge' | 'icon' | 'custom';
  badgeConfig?: {
    values: { [key: string]: { class: string; icon?: string } };
  };
  customTemplate?: TemplateRef<any>;
  sortable?: boolean;
  width?: string;
}

export interface TableAction {
  id: string;
  label: string;
  icon?: string;
  iconClass?: string;
  action: (item: any) => void;
  isAvailable?: (item: any) => boolean;
}

export interface MassAction {
  id: string;
  label: string;
  icon?: string;
  iconClass?: string;
  action: (items: any[]) => void;
  confirmMessage?: string;
  confirmIcon?: string;
}

export interface TableConfig {
  type: string;
  idField: string;
  actions?: TableAction[];
  massActions?: MassAction[];
  defaultSort?: { field: string; direction: 'asc' | 'desc' };
  itemsPerPageOptions?: number[];
  defaultItemsPerPage?: number;
  stateSummary?: { state: string; count: number }[];
  stateField?: string;
  availableStates?: { [key: string]: string[] };
}

@Component({
  selector: 'app-data-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class DataTableComponent implements OnInit, OnChanges {
  @ViewChild('actionConfirmModal') _actionConfirmModal: Modal;
  @Input() items: any[] = [];
  @Input() columnConfig: ColumnConfig[] = [];
  @Input() tableConfig: TableConfig;
  @Input() selectedItems: any[] = [];
  @Input() selectAll: boolean = false;
  @Input() currentPage: number = 1;
  @Input() itemsPerPage: number = 10;
  @Input() itemsPerPageOptions: number[] = [5, 10, 25, 50];

  @Output() selectAllChange = new EventEmitter<boolean>();
  @Output() itemSelectionChange = new EventEmitter<any[]>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() itemsPerPageChange = new EventEmitter<void>();
  @Output() loadList = new EventEmitter<void>();
  @Output() actionExecuted = new EventEmitter<{action: string; items: any[]}>();

  Math = Math;
  paginatedItems: any[] = [];
  private checkboxIds: Map<string, string> = new Map();
  private selectAllId: string;
  availableActions: Map<string, TableAction[]> = new Map();
  menuButtonList: LabelValue<number>[] = [
    { label: this._appService.CoreCommonLiterals.MultiSelect_All_Male, value: 1 },
    { label: this._appService.CoreCommonLiterals.None_Male, value: 2 },
  ];

  stateSummaryArray: { count: number; state: string }[] = [];
  showMassActionConfirm = false;
  pendingMassAction: MassAction | null = null;

  constructor(
    public _appService: AppService,
    private toasterService: ToasterService
  ) {}

  ngOnInit() {
    this.selectAllId = `check-all-${this.tableConfig.type}-${Date.now()}`;
    this.updatePaginatedItems();
    
    this.items.forEach(item => {
      this.updateAvailableActions(item);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items'] || changes['currentPage'] || changes['itemsPerPage']) {
      this.updatePaginatedItems();
    }
    
    if (changes['tableConfig'] && changes['tableConfig'].currentValue?.actions) {
      this.items.forEach(item => {
        this.updateAvailableActions(item);
      });
    }
  }

  getTotalPages(): number {
    return Math.ceil(this.items.length / this.itemsPerPage);
  }

  private updatePaginatedItems(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedItems = this.items.slice(startIndex, startIndex + this.itemsPerPage);
    
    this.paginatedItems.forEach(item => {
      if (!this.checkboxIds.has(item[this.tableConfig.idField])) {
        this.checkboxIds.set(item[this.tableConfig.idField], 
         `check-${this.tableConfig.type}-${item[this.tableConfig.idField]}-${Date.now()}`);
      }
      this.updateAvailableActions(item);
    });
  }

  private getAvailableStatesForItem(item: any): TableAction[] {
    if (!this.tableConfig.actions) return [];

    return this.tableConfig.actions.filter(action => {
      if (!action.isAvailable) return true;
      try {
        return action.isAvailable(item);
      } catch (error) {
        console.error('Error al evaluar disponibilidad de acción:', error);
        return false;
      }
    });
  }

  updateAvailableActions(item: any): void {
    if (!this.tableConfig.actions) return;
    
    const availableActions = this.getAvailableStatesForItem(item);
    this.availableActions.set(item[this.tableConfig.idField], availableActions);
  }

  getAvailableActions(item: any): TableAction[] {
    if (!this.availableActions.has(item[this.tableConfig.idField])) {
      this.updateAvailableActions(item);
    }
    return this.availableActions.get(item[this.tableConfig.idField]) || [];
  }

  getCheckboxId(item: any = null): string {
    if (item === null) {
      return this.selectAllId;
    }
    return this.checkboxIds.get(item[this.tableConfig.idField]) || 
      `check-${this.tableConfig.type}-${item[this.tableConfig.idField]}-${Date.now()}`;
  }

  onSelectAllChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.items = this.items.map(item => ({
      ...item,
      selected: checked
    }));

    this.paginatedItems = this.paginatedItems.map(item => ({
      ...item,
      selected: checked
    }));

    this.selectedItems = checked ? [...this.items] : [];
    this.selectAll = checked;

    this.selectAllChange.emit(checked);
    this.itemSelectionChange.emit(this.paginatedItems);
    this.updateStateSummary();
  }

  onItemSelectionChange(): void {
    this.selectedItems = this.items.filter(item => item.selected);
    this.selectAll = this.items.length > 0 && this.items.every(item => item.selected);
    this.itemSelectionChange.emit(this.paginatedItems);
    this.updateStateSummary();
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onItemsPerPageChange(): void {
    this.itemsPerPageChange.emit();
  }

  onLoadList(): void {
    this.loadList.emit();
  }

  executeAction(action: TableAction, item: any): void {
    try {
      action.action(item);
      this.updateAvailableActions(item);
      this.actionExecuted.emit({ action: action.id, items: [item] });
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      this.toasterService.pop('error', 'Error', 'No se pudo ejecutar la acción');
    }
  }

  initiateMassAction(action: MassAction): void {
    if (action.confirmMessage) {
      this.pendingMassAction = action;
      this._actionConfirmModal.open();
    } else {
      this.executeMassAction(action);
    }
  }

  executeMassAction(action: MassAction): void {
    action.action(this.selectedItems);
    this.actionExecuted.emit({ action: action.id, items: this.selectedItems });

    this._actionConfirmModal.close();
    this.pendingMassAction = null;
    
    this.selectedItems = [];
    this.selectAll = false;
    
    this.items = this.items.map(item => ({
      ...item,
      selected: false
    }));
    
    this.paginatedItems = this.paginatedItems.map(item => ({
      ...item,
      selected: false
    }));
    
    this.paginatedItems.forEach(item => {
      this.updateAvailableActions(item);
    });
    
    this.updateStateSummary();
    
    this.selectAllChange.emit(false);
    this.itemSelectionChange.emit(this.paginatedItems);
    
    this.toasterService.pop('success', 'Éxito', 
      `Acción "${action.label}" ejecutada en ${this.selectedItems.length} elementos`);
  }

  cancelMassAction(): void {
    this._actionConfirmModal.close();
    this.pendingMassAction = null;
  }

  updateStateSummary(): void {
    if (!this.selectedItems.length) {
      this.stateSummaryArray = [];
      return;
    }

    const stateCount = new Map<string, number>();
    
    this.selectedItems.forEach(item => {
      let state: string;
      
      switch (this.tableConfig.type) {
        case 'valvula':
          state = item.estadoValvula?.toLowerCase() || 'desconocido';
          break;
        case 'alarma':
          state = item.estado?.toLowerCase() || 'desconocido';
          break;
        case 'dispositivo':
          state = item.estadoDispositivo?.toLowerCase() || 'desconocido';
          break;
        default:
          state = item.estado?.toLowerCase() || 'desconocido';
      }

      stateCount.set(state, (stateCount.get(state) || 0) + 1);
    });

    this.stateSummaryArray = Array.from(stateCount.entries())
      .filter(([state]) => state !== 'desconocido')
      .map(([state, count]) => ({
        state: state.charAt(0).toUpperCase() + state.slice(1),
        count
      }));
  }

  getBadgeClass(item: any, column: ColumnConfig): string {
    if (column.badgeConfig && column.badgeConfig.values) {
      const value = item[column.property]?.toLowerCase();
      return column.badgeConfig.values[value]?.class || '';
    }
    return '';
  }

  getBadgeIcon(item: any, column: ColumnConfig): string {
    if (column.badgeConfig && column.badgeConfig.values) {
      const value = item[column.property]?.toLowerCase();
      return column.badgeConfig.values[value]?.icon || '';
    }
    return '';
  }

  dropdownMenuAction(event: any, item: LabelValue<number>): void {
    switch (item.value) {
      case 1:
        this.selectAll = true;
        const mockEvent = { target: { checked: true } } as unknown as Event;
        this.onSelectAllChange(mockEvent);
        break;
      case 2:
        this.selectAll = false;
        this.onSelectAllChange(event);
        break;
      default:
        break;
    }
  }
} 